/* src/components/journal/JournalEntry.js */
import React from 'react';
import { formatDateTime, formatDuration } from '../dashboard/utils/formatters'; // Ensure path is correct
import SnapshotChartDisplay from './SnapshotChartDisplay'; // Component for static chart rendering
import { formatCombinedDifficulty } from '../../contexts/gameHelpers'; // Helper for level display

/**
 * Renders a single entry card within the journal list.
 * Adapts display based on entry type ('manual', 'daily_summary', 'chart_snapshot', 'session_summary').
 * Provides "Edit" (manual, session_summary, chart_snapshot) and "Delete" (manual, snapshot, session_summary) buttons.
 * Includes "Watch Replay" button for session summaries if replay data exists.
 * Styled to match FriendsPage list items.
 *
 * Props:
 * - entry: {object} The journal entry object containing data such as:
 *    - id: {string} Unique identifier for the entry.
 *    - type: {string} Type of entry ('manual', 'daily_summary', 'chart_snapshot', 'session_summary').
 *    - timestamp: {string} ISO 8601 timestamp of entry creation/snapshot.
 *    - content?: {string} Text content for manual entries or optional comment for session/chart entries.
 *    - stats?: {object} Statistics object for daily_summary entries.
 *    - snapshotData?: {object} Data object for chart_snapshot entries.
 *    - summaryData?: {object} Data object for session_summary entries containing detailed performance.
 *    - tags?: {Array<string>} Array of tag strings associated with the entry.
 *    - date?: {string} Specific date string (YYYY-MM-DD) for daily_summary entries.
 * - onEdit: {function} Callback function triggered when the Edit button is clicked. Receives the entry ID.
 * - onDelete: {function} Callback function triggered when the Delete button is clicked. Receives the entry ID.
 * - onNavigate: {function} Callback function for navigation (passed from JournalPage, needed for replay).
 */
const JournalEntry = ({ entry, onEdit, onDelete, onNavigate }) => {
  // Destructure all potential fields from the entry object for easier access, providing defaults
  const {
    id,
    type,
    timestamp,
    content = '',        // Content for manual entries OR comments for session/chart summaries
    stats = null,        // Data for daily summaries
    snapshotData = null, // Data for chart snapshots
    summaryData = null,  // Data for session summaries (now includes detailed metrics)
    tags = [],           // Array of tags
    date = ''            // Specific date for daily summaries
  } = entry;

  // Helper function to format numbers safely, default to 1 decimal place
  const formatNum = (num, decimals = 1) => (num !== null && num !== undefined && typeof num === 'number' && Number.isFinite(num) ? num.toFixed(decimals) : 'N/A');
  // Specific formatter for d' values (2 decimal places)
  const formatDprime = (dp) => formatNum(dp, 2);

  /**
   * Renders the list of tags associated with the entry.
   * @returns {React.ReactElement | null} A div containing tag elements, or null if no tags exist.
   */
  const renderTags = () => {
    if (!Array.isArray(tags) || tags.length === 0) {
        return null;
    }
    return (
        // Use lighter border for separation
        <div className="flex flex-wrap gap-1 mt-3 pt-2 border-t border-gray-100">
        {tags.map((tag, index) => (
            <span
                key={`${id}-tag-${index}`} // Unique key for each tag span
                className="bg-gray-200 text-gray-700 text-xs font-medium px-2 py-0.5 rounded-full cursor-default"
                title={`Tag: ${tag}`} // Tooltip for the tag
            >
            {tag} {/* Display the tag text */}
            </span>
        ))}
        </div>
    );
  };

  /**
   * Renders the header section of the journal entry card.
   * Displays the entry type/title, timestamp, and Edit/Delete buttons where applicable.
   * @returns {React.ReactElement} The header div element.
   */
  const renderHeader = () => {
    let titleText = 'Manual Entry';
    let titleColor = 'text-green-600';

    // Determine title and color based on the entry type
    if (type === 'daily_summary') {
      titleText = `Daily Summary (${date || 'Unknown Date'})`;
      titleColor = 'text-blue-600';
    } else if (type === 'chart_snapshot') {
      const metricName = snapshotData?.metricName || 'Metric';
      const periodName = snapshotData?.periodName || 'Period';
      titleText = `Chart: ${metricName} (${periodName})`;
      titleColor = 'text-teal-600';
    } else if (type === 'session_summary') {
        // Use the 'old' N-back/difficulty from summaryData to show the level played
        const level = formatCombinedDifficulty(summaryData?.oldNBack ?? '?', summaryData?.oldDifficulty ?? '?');
        titleText = `Session Summary (Lvl ${level})`;
        titleColor = 'text-purple-600'; // Distinct color for session summaries
    }

    // --- MODIFIED canEdit check ---
    // Allow editing for 'manual', 'session_summary', AND 'chart_snapshot' types.
    const canEdit = (type === 'manual' || type === 'session_summary' || type === 'chart_snapshot') &&
                    typeof onEdit === 'function';

    // Delete remains allowed for 'manual', 'chart_snapshot', 'session_summary'
    const canDelete = (type === 'manual' || type === 'chart_snapshot' || type === 'session_summary') &&
                      typeof onDelete === 'function';

    return (
      // Adjusted header styling
      <div className="flex justify-between items-start mb-2 pb-2 border-b border-gray-100">
        {/* Left side: Title and Timestamp */}
        <div>
          <span className={`block text-xs font-semibold uppercase tracking-wider mb-1 ${titleColor}`}>
            {titleText}
          </span>
          <span className="text-xs text-gray-500" title={new Date(timestamp).toLocaleString()}>
            {formatDateTime(timestamp)} {/* Use date formatting utility */}
          </span>
        </div>
        {/* Right side: Action Buttons Container */}
        {(canEdit || canDelete) && (
            // Adjusted button styling to match FriendsPage small actions
            <div className="flex items-center space-x-2 flex-shrink-0">
            {/* Edit Button (Now shows for chart snapshots too) */}
            {canEdit && (
                <button
                    onClick={() => onEdit(id)} // Trigger edit action
                    className="text-xs text-indigo-600 hover:text-indigo-800 hover:underline px-1 py-0.5 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    aria-label="Edit" title="Edit" // Updated tooltip/label
                >
                    Edit
                </button>
            )}
            {/* Delete Button */}
            {canDelete && (
                 <button
                    onClick={() => onDelete(id)} // Trigger delete action
                    className="text-xs text-red-600 hover:text-red-800 hover:underline px-1 py-0.5 rounded focus:outline-none focus:ring-1 focus:ring-red-500"
                    aria-label="Delete" title="Delete" // Updated tooltip/label
                 >
                     Delete
                 </button>
            )}
            </div>
        )}
      </div>
    );
  };

  /**
   * Renders the main content area of the journal entry card based on its type.
   * @returns {React.ReactElement | null} The content element or null.
   */
  const renderContent = () => {
    switch (type) {
      case 'manual':
        // Display plain text content, preserving whitespace formatting
        return content ? <p className="text-sm text-gray-800 whitespace-pre-wrap my-2">{content}</p> : null;

      case 'daily_summary':
        // Display formatted statistics for daily summaries
        return stats ? (
          // Use text-xs for details
          <div className="text-xs text-gray-700 my-2">
            <div className="grid grid-cols-[auto,1fr] gap-x-2 gap-y-1">
              <strong className="text-right font-medium text-gray-600">Sessions:</strong> <span>{stats.totalSessions ?? 'N/A'}</span>
              <strong className="text-right font-medium text-gray-600">Total Time:</strong> <span>{formatDuration(stats.totalDurationMs) ?? 'N/A'}</span>
              <strong className="text-right font-medium text-gray-600">Avg Level:</strong> <span>{stats.avgStartLevel ?? 'N/A'} (High: {stats.highestEndLevel ?? 'N/A'})</span>
              <strong className="text-right font-medium text-gray-600">Avg Acc:</strong> <span>{formatNum(stats.avgAccuracy, 1)}%</span> {/* Simplified Accuracy display */}
              <strong className="text-right font-medium text-gray-600">Avg d':</strong> <span>{formatNum(stats.avgDprime, 2)}</span>
            </div>
          </div>
        ) : <p className="text-sm text-gray-500 italic my-2">Daily summary data not available.</p>;

      case 'chart_snapshot':
        // Render the static chart component for chart snapshots
        return snapshotData ? (
          <>
            <SnapshotChartDisplay snapshotData={snapshotData} />
            {/* Display any associated manual content if added later via edit */}
            {content && content.trim() && (
                <p className="text-sm text-gray-700 mt-3 pt-3 border-t border-gray-100 whitespace-pre-wrap">
                    {content}
                </p>
            )}
          </>
        ) : <p className="text-sm text-red-600 italic my-2">Error: Chart snapshot data missing or invalid.</p>;

      case 'session_summary':
        // Check if the necessary summary data exists
        if (!summaryData) {
            return <p className="text-sm text-red-600 italic my-2">Error: Session summary data missing.</p>;
        }
        // Extract all the detailed metrics and context from summaryData
        const {
            oldNBack, oldDifficulty, newNBack, newDifficulty, durationMs,
            trialsCompleted, totalTrialsPlanned, overallDprime,
            visualAccuracy, visualCorrect, visualTargetCount, visualFalseAlarms, visualDprime,
            audioAccuracy, audioCorrect, audioTargetCount, audioFalseAlarms, audioDprime,
            excellentThreshold, goodThreshold, fairThreshold,
            replayNavData // Extract the replayNavData object directly
        } = summaryData;

        // Format common values for display
        const oldLevel = formatCombinedDifficulty(oldNBack, oldDifficulty);
        const newLevel = formatCombinedDifficulty(newNBack, newDifficulty);
        const durationDisplay = formatDuration(durationMs);
        const trialsDisplay = `${trialsCompleted}/${totalTrialsPlanned}`;

        // Check if replayNavData exists and seems structurally sound
        const hasReplayData = !!replayNavData &&
                              !!replayNavData.settings &&
                              Array.isArray(replayNavData.events) && // Ensure events is an array
                              replayNavData.startTime !== null &&
                              replayNavData.startTime !== undefined;

        // Handler for the replay button click
        const handleReplayClick = () => {
            // Use summaryData.sessionId if available, fallback to entry.id (though less reliable for replays)
            const replaySessionId = summaryData?.sessionId || id;
            if (hasReplayData && typeof onNavigate === 'function' && replaySessionId) {
                 // Navigate to the standard replay route using the session ID
                onNavigate(`/replay/${replaySessionId}`);
            } else {
                console.warn("Replay conditions not met:", { hasReplayData, onNavigateType: typeof onNavigate, replaySessionId });
                window.alert("Could not start replay for this entry."); // Use standard browser alert
            }
        };

        // Render the detailed session summary view
        return (
            // Adjusted spacing
            <div className="my-2 space-y-3">
                {/* Overall Summary Row - Adjusted spacing and border */}
                <div className="flex flex-wrap justify-between items-baseline text-xs text-gray-600 border-b border-gray-100 pb-2 mb-2 gap-x-3 gap-y-1">
                    <span>Lvl: <span className="font-medium">{oldLevel} → {newLevel}</span></span>
                    <span>d′: <span className="font-medium">{formatDprime(overallDprime)}</span></span>
                    <span>⏱️ <span className="font-medium">{durationDisplay}</span></span>
                    <span>Trials: <span className="font-medium">{trialsDisplay}</span></span>
                </div>

                {/* Detailed Performance Grid - Adjusted gaps and padding */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                    {/* Visual Performance Box */}
                    <div className="bg-gray-50 border border-gray-200 p-2 rounded"> {/* Reduced padding */}
                        <div className="font-semibold mb-1 text-blue-700 text-xs">Visual</div>
                        <div className="space-y-0.5"> {/* Reduced spacing */}
                            <div className="flex justify-between"><span className="text-gray-600">Accuracy:</span><span className="font-medium">{formatNum(visualAccuracy, 1)}%</span></div>
                            <div className="flex justify-between"><span className="text-gray-600">Hits:</span><span>{visualCorrect ?? '?'} / {visualTargetCount ?? '?'}</span></div>
                            <div className="flex justify-between"><span className="text-gray-600">False Alarms:</span><span>{visualFalseAlarms ?? '?'}</span></div>
                            <div className="flex justify-between pt-1 border-t mt-1"><span className="text-gray-600">d′:</span><span className="font-medium">{formatDprime(visualDprime)}</span></div>
                        </div>
                    </div>
                    {/* Audio Performance Box */}
                    <div className="bg-gray-50 border border-gray-200 p-2 rounded"> {/* Reduced padding */}
                        <div className="font-semibold mb-1 text-green-700 text-xs">Audio</div>
                         <div className="space-y-0.5"> {/* Reduced spacing */}
                            <div className="flex justify-between"><span className="text-gray-600">Accuracy:</span><span className="font-medium">{formatNum(audioAccuracy, 1)}%</span></div>
                            <div className="flex justify-between"><span className="text-gray-600">Hits:</span><span>{audioCorrect ?? '?'} / {audioTargetCount ?? '?'}</span></div>
                            <div className="flex justify-between"><span className="text-gray-600">False Alarms:</span><span>{audioFalseAlarms ?? '?'}</span></div>
                            <div className="flex justify-between pt-1 border-t mt-1"><span className="text-gray-600">d′:</span><span className="font-medium">{formatDprime(audioDprime)}</span></div>
                        </div>
                    </div>
                </div>

                {/* d' Interpretation Ranges */}
                {(excellentThreshold !== undefined && goodThreshold !== undefined && fairThreshold !== undefined) && (
                    <div className="text-xs text-gray-500 mt-2 p-1.5 bg-blue-50 rounded border border-blue-100 text-center"> {/* Adjusted padding/margin */}
                        <span className="font-medium text-gray-600">Guide:</span>{' '}
                        Exc≥{formatDprime(excellentThreshold)} | {' '}
                        Good≥{formatDprime(goodThreshold)} | {' '}
                        Fair≥{formatDprime(fairThreshold)}
                    </div>
                )}

                {/* Replay Button (Conditional) - Styled like small action buttons */}
                {hasReplayData && (
                    <div className="text-right mt-2">
                         <button onClick={handleReplayClick} className="px-2 py-0.5 bg-teal-100 text-teal-700 text-xs rounded hover:bg-teal-200 focus:outline-none focus:ring-1 focus:ring-teal-500 transition-colors duration-150 inline-flex items-center" title="Replay">
                            <svg className="w-3 h-3 inline -ml-0.5 mr-1" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd"></path></svg>
                            Replay
                        </button>
                    </div>
                )}

                {/* Display Optional Comment */}
                {content && content.trim() && (
                    <p className="text-sm text-gray-800 mt-3 pt-3 border-t border-gray-100 whitespace-pre-wrap">
                        {content}
                    </p>
                )}
            </div>
        );

      default:
        // Fallback for any unrecognized entry types
        console.warn(`JournalEntry encountered unknown type: ${type} for entry ID: ${id}`);
        return <p className="text-sm text-red-600 italic my-2">Unknown entry type encountered.</p>;
    }
  };

  // Render the complete entry card structure
  return (
    // Main entry card - Adjusted spacing and border/shadow
    <div className="mb-2 p-4 border border-gray-200 rounded-lg bg-white shadow-sm transition-shadow hover:shadow-md">
      {renderHeader()}   {/* Render the header section */}
      {renderContent()}  {/* Render the main content based on entry type */}
      {renderTags()}     {/* Render the tags section if tags exist */}
    </div>
  );
};

export default JournalEntry;